import React, { useMemo, useState } from "react";
import { CheckCircledIcon, ImageIcon } from "@radix-ui/react-icons";
import { RadixButton } from "../primitives/RadixButton";
import { RadixFlyOut } from "../primitives/RadixFlyOut";
import { isAvailableForOrderMode, isCateringOrderMode, normalizeCateringMenuAvailability, } from "../utility/cateringMenuAvailability";
import "./dealsSelection.css";
var PENDING_DEAL_STORAGE_KEY = "pending_deal_code";
var DEFAULT_PLACEHOLDER_IMAGE = "https://d2tiuz65ev218l.cloudfront.net/toppings/generic.svg";
var normalizeDeal = function (candidate, index) {
    if (!candidate || typeof candidate !== "object") {
        return null;
    }
    var dealRecord = candidate;
    var code = String(dealRecord.code || "").trim();
    if (!code) {
        return null;
    }
    return {
        id: String(dealRecord.id || code || index),
        code: code,
        title: String(dealRecord.deal_title || dealRecord.title || "Deal").trim() || "Deal",
        dealText: String(dealRecord.deal_text || dealRecord.description || "").trim(),
        subtext: String(dealRecord.subtext || "").trim(),
        imageSrc: String(dealRecord.img_src || "").trim() || DEFAULT_PLACEHOLDER_IMAGE,
        imageWidth: Number(dealRecord.img_width) || 320,
        imageHeight: Number(dealRecord.img_height) || 200,
        cateringMenuAvailability: normalizeCateringMenuAvailability(dealRecord.catering_menu_availability),
    };
};
var isCateringOrder = function () {
    var _a, _b;
    var value = (_b = (_a = window.session) === null || _a === void 0 ? void 0 : _a.store) === null || _b === void 0 ? void 0 : _b.is_catering;
    return isCateringOrderMode(value);
};
export var setPendingDealCode = function (code) {
    if (typeof window === "undefined") {
        return;
    }
    var normalizedCode = String(code || "").trim();
    if (!normalizedCode) {
        return;
    }
    window.localStorage.setItem(PENDING_DEAL_STORAGE_KEY, normalizedCode);
};
export var getPendingDealCode = function () {
    if (typeof window === "undefined") {
        return "";
    }
    return String(window.localStorage.getItem(PENDING_DEAL_STORAGE_KEY) || "").trim();
};
export var clearPendingDealCode = function () {
    if (typeof window === "undefined") {
        return;
    }
    window.localStorage.removeItem(PENDING_DEAL_STORAGE_KEY);
};
export var DealsSelection = function (_a) {
    var deals = _a.deals, _b = _a.open, open = _b === void 0 ? false : _b, onOpenChange = _a.onOpenChange, onSelectionComplete = _a.onSelectionComplete, onPendingDealApply = _a.onPendingDealApply, _c = _a.hideHeader, hideHeader = _c === void 0 ? false : _c, _d = _a.presentation, presentation = _d === void 0 ? "inline" : _d;
    var _e = useState(function () { return getPendingDealCode(); }), selectedDealCode = _e[0], setSelectedDealCode = _e[1];
    var _f = useState(""), feedbackMessage = _f[0], setFeedbackMessage = _f[1];
    var _g = useState(null), feedbackType = _g[0], setFeedbackType = _g[1];
    var normalizedDeals = useMemo(function () {
        var cateringOrder = isCateringOrder();
        return deals
            .map(function (deal, index) { return normalizeDeal(deal, index); })
            .filter(function (deal) { return deal !== null; })
            .filter(function (deal) { return isAvailableForOrderMode(deal.cateringMenuAvailability, cateringOrder); });
    }, [deals]);
    if (normalizedDeals.length === 0) {
        return null;
    }
    var handleApplyAtCheckout = function (deal) {
        var selectedCode = selectedDealCode.trim().toLowerCase();
        var nextCode = deal.code.trim().toLowerCase();
        if (selectedCode === nextCode) {
            clearPendingDealCode();
            setSelectedDealCode("");
            setFeedbackMessage("Promo code ".concat(deal.code, " selection cleared."));
            setFeedbackType("success");
            return;
        }
        setPendingDealCode(deal.code);
        setSelectedDealCode(deal.code);
        onPendingDealApply === null || onPendingDealApply === void 0 ? void 0 : onPendingDealApply(deal);
        setFeedbackMessage("Promo code ".concat(deal.code, " will be ready for checkout."));
        setFeedbackType("success");
        onSelectionComplete === null || onSelectionComplete === void 0 ? void 0 : onSelectionComplete();
    };
    var content = (React.createElement("section", { className: [
            "deals-selection",
            presentation === "inline" ? "category-navigation-section" : null,
            presentation === "flyout" ? "deals-selection--flyout" : null,
        ].filter(Boolean).join(" "), "data-category-id": presentation === "inline" ? "deals" : undefined, "aria-labelledby": "deals-selection-heading" },
        !hideHeader ? (React.createElement("div", { className: "deals-selection__header" },
            React.createElement("h2", { id: "deals-selection-heading", className: "theme-primary-text deals-selection__heading" }, "Promos"),
            React.createElement("p", { className: "deals-selection__copy" }, "Choose a deal and apply its promo code at checkout."))) : null,
        React.createElement("ul", { className: "deals-selection__list", "aria-label": "Available deals" }, normalizedDeals.map(function (deal) {
            var hasSelectedDeal = selectedDealCode.trim().length > 0;
            var isSelectedDeal = selectedDealCode.trim().toLowerCase() === deal.code.trim().toLowerCase();
            var shouldGreyOutDeal = hasSelectedDeal && !isSelectedDeal;
            var isDisabled = shouldGreyOutDeal;
            return (React.createElement("li", { key: deal.id, className: ["deals-selection__item", shouldGreyOutDeal ? "deals-selection__item--inactive" : null].filter(Boolean).join(" ") },
                React.createElement("article", { className: ["deals-selection__card", isSelectedDeal ? "deals-selection__card--selected" : null].filter(Boolean).join(" ") },
                    React.createElement("div", { className: "deals-selection__image-wrap" },
                        React.createElement("img", { className: "deals-selection__image", src: deal.imageSrc, width: deal.imageWidth, height: deal.imageHeight, alt: "".concat(deal.title, " deal"), onError: function (event) {
                                event.currentTarget.src = DEFAULT_PLACEHOLDER_IMAGE;
                            } }),
                        deal.imageSrc === DEFAULT_PLACEHOLDER_IMAGE ? (React.createElement("span", { className: "deals-selection__image-badge", "aria-hidden": "true" },
                            React.createElement(ImageIcon, null))) : null),
                    React.createElement("div", { className: "deals-selection__content" },
                        React.createElement("div", { className: "deals-selection__text" },
                            React.createElement("h3", { className: "deals-selection__title" }, deal.title),
                            deal.dealText ? React.createElement("p", { className: "deals-selection__description" }, deal.dealText) : null,
                            deal.subtext ? React.createElement("p", { className: "deals-selection__subtext" }, deal.subtext) : null,
                            React.createElement("p", { className: "deals-selection__code" },
                                "Promo code: ",
                                React.createElement("strong", null, deal.code))),
                        React.createElement(RadixButton, { type: "button", className: "deals-selection__button", onClick: function () {
                                handleApplyAtCheckout(deal);
                            }, disabled: isDisabled, "aria-label": isSelectedDeal ? "Undo selected deal ".concat(deal.title) : "Apply deal ".concat(deal.title, " using promo code ").concat(deal.code) }, isSelectedDeal ? "Undo selection" : "Apply at checkout")))));
        })),
        React.createElement("p", { className: ["deals-selection__feedback", feedbackType ? "deals-selection__feedback--".concat(feedbackType) : null].filter(Boolean).join(" "), role: "status", "aria-live": "polite" }, feedbackMessage ? React.createElement(React.Fragment, null,
            React.createElement(CheckCircledIcon, { "aria-hidden": "true" }),
            " ",
            feedbackMessage) : null)));
    if (presentation === "flyout") {
        return (React.createElement(RadixFlyOut, { open: open, onOpenChange: function (nextOpen) {
                onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(nextOpen);
            }, title: "Promos" }, content));
    }
    return content;
};
